import React, { useState, useEffect } from 'react'
import { useShoppingList, clientLogger, ICONS, getIconSize } from '@talktoast/shared'
import { backendApiService } from '@talktoast/shared'
import { Button, Card, Loading } from '@talktoast/ui'
import { showSuccess } from '../utils/toast'

interface ShoppingActionsScreenProps {
  onBack?: () => void
  onContinueToCalendar?: () => void
}

const ShoppingActionsScreen: React.FC<ShoppingActionsScreenProps> = ({
  onBack,
  onContinueToCalendar
}) => {
  const { activeList } = useShoppingList()
  const [instacartLoading, setInstacartLoading] = useState(false)
  const [instacartError, setInstacartError] = useState<string | null>(null)
  const [instacartRetryable, setInstacartRetryable] = useState(false)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  const handleDownload = () => {
    if (!activeList) return

    // Create downloadable text content (exclude checked items)
    // Use client-side date for download timestamp (date only, no time)
    const content = [
      `Shopping List: ${activeList.name}`,
      `Date: ${new Date().toLocaleDateString()}`,
      '',
      ...activeList.items
        .filter(item => !item.checked)
        .map(item =>
          `• ${item.name} - ${item.purchaseGuidance || [item.amount != null ? String(item.amount) : null, item.unit].filter(Boolean).join(' ') || 'qty unavailable'}`
        )
    ].join('\n')

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${activeList.name.replace(/\s+/g, '_')}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleShare = async () => {
    if (!activeList) return

    const content = [
      `Shopping List: ${activeList.name}`,
      '',
      ...activeList.items
        .filter(item => !item.checked)
        .map(item =>
          `• ${item.name} - ${item.purchaseGuidance || [item.amount != null ? String(item.amount) : null, item.unit].filter(Boolean).join(' ') || 'qty unavailable'}`
        )
    ].join('\n')

    if (navigator.share) {
      try {
        await navigator.share({
          title: activeList.name,
          text: content
        })
      } catch (error) {
        console.log('Share cancelled or failed')
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(content)
      showSuccess('Shopping list copied to clipboard!')
    }
  }

  const handleInstacart = async () => {
    if (!activeList) return

    try {
      setInstacartLoading(true)
      setInstacartError(null)
      setInstacartRetryable(false)

      clientLogger.debug({}, 'Creating Instacart link')
      const instacartUrl = await backendApiService.createInstacartLink(activeList)

      clientLogger.debug({ url: instacartUrl }, 'Opening Instacart link')
      window.open(instacartUrl, '_blank')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Instacart link creation failed')
      const isRetryable = (error as Error & { retryable?: boolean }).retryable === true
      setInstacartRetryable(isRetryable)
      setInstacartError(
        error instanceof Error
          ? error.message
          : 'Unable to create Instacart link. Please try again.'
      )
    } finally {
      setInstacartLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={onBack || (() => window.history.back())}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              ← Back
            </button>
          </div>

          <h1 className="text-xl font-bold text-gray-900 mb-1">
            Get Your Groceries
          </h1>

          <p className="text-gray-600 text-sm">
            Choose how you'd like to shop for your meal plan
          </p>
        </div>
      </div>

      {/* Action Options */}
      <div className="max-w-md mx-auto px-4 py-6 space-y-4">

        {/* Delivery Services */}
        <Card variant="elevated">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="font-semibold text-gray-900">🚚 Delivery Services</h2>
            <p className="text-sm text-gray-600">Order online and get delivered</p>
          </div>

          <div className="p-4 space-y-3">
            {/* Instacart Integration */}
            <button
              onClick={handleInstacart}
              disabled={instacartLoading || !activeList}
              className="w-full flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 text-lg">🥬</span>
                </div>
                <div className="text-left">
                  <div className="font-medium text-green-900">Instacart</div>
                  <div className="text-sm text-green-700">Same-day delivery</div>
                </div>
              </div>
              {instacartLoading ? (
                <Loading variant="spinner" size="sm" />
              ) : (
                <span className="text-green-600 font-bold">→</span>
              )}
            </button>

            {/* Instacart Error Message */}
            {instacartError && (
              <Card variant="outlined" className="bg-red-50 border-red-200">
                <div className="flex items-start gap-3 p-1">
                  <ICONS.feedback.warning className={`${getIconSize('sm')} text-red-600 mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-red-800">{instacartError}</p>
                    {instacartRetryable && (
                      <button
                        onClick={handleInstacart}
                        disabled={instacartLoading}
                        className="mt-2 flex items-center gap-1.5 text-sm font-medium text-toast-brown hover:text-toast-gold-active transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ICONS.actions.refresh className={getIconSize('sm')} />
                        Try Again
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            )}

            <button className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 text-lg">🏪</span>
                </div>
                <div className="text-left">
                  <div className="font-medium">Kroger</div>
                  <div className="text-sm text-gray-500">Pickup & delivery</div>
                </div>
              </div>
              <span className="text-gray-400">Coming Soon</span>
            </button>

            <button className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <span className="text-red-600 text-lg">🍕</span>
                </div>
                <div className="text-left">
                  <div className="font-medium">DoorDash</div>
                  <div className="text-sm text-gray-500">Quick delivery</div>
                </div>
              </div>
              <span className="text-gray-400">Coming Soon</span>
            </button>
          </div>
        </Card>

        {/* Share & Export */}
        <Card variant="elevated">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="font-semibold text-gray-900">📱 Share & Export</h2>
            <p className="text-sm text-gray-600">Take your list with you</p>
          </div>

          <div className="p-4 space-y-3">
            <button
              onClick={handleDownload}
              className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-purple-600 text-lg">⬇️</span>
                </div>
                <div className="text-left">
                  <div className="font-medium">Download</div>
                  <div className="text-sm text-gray-500">Save as text file</div>
                </div>
              </div>
              <span className="text-gray-400">→</span>
            </button>

            <button
              onClick={handleShare}
              className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-orange-600 text-lg">📤</span>
                </div>
                <div className="text-left">
                  <div className="font-medium">Share</div>
                  <div className="text-sm text-gray-500">Send to family/friends</div>
                </div>
              </div>
              <span className="text-gray-400">→</span>
            </button>
          </div>
        </Card>

        {/* Continue to Planning */}
        <Card variant="elevated">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h2 className="font-semibold text-gray-900">📅 Meal Planning</h2>
            <p className="text-sm text-gray-600">Schedule your meals</p>
          </div>

          <div className="p-4">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={onContinueToCalendar}
              className="justify-between bg-blue-600 hover:bg-blue-700"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg">📅</span>
                <div className="text-left">
                  <div className="font-medium">Schedule Meals</div>
                  <div className="text-xs opacity-90">Assign meals to days of the week</div>
                </div>
              </div>
              <span className="font-bold">→</span>
            </Button>
          </div>
        </Card>
      </div>

      {/* Bottom spacing for mobile navigation */}
      <div className="h-20"></div>
    </div>
  )
}

export default ShoppingActionsScreen